/**
 * WarrantAlertStrip — Round P142·3 (Feb 2026)
 *
 * Compact one-line alert rail rendered BELOW a watchlist row when
 * the row is a warrant/unit AND at least one of these conditions
 * fires (highest priority wins):
 *
 *   1. Force-redeemed  (`warrant_status === "redeemed"`)
 *   2. Redemption 8-K announced (`warrant_status === "redemption_announced"`)
 *   3. Under 30 days to expiry  (`warrant_expiration_date - today <= 30`)  ← P142·3
 *   4. Near strike — underlying within 5% of exercise price
 *
 * Renders nothing when none fire so unrelated rows stay clean.
 * Per privacy policy this is ON-SITE ONLY — no email alerts.
 *
 * Data source: values are already stamped onto `stock_cache` by
 * `services/warrants_pipeline.py`:
 *   - warrant_status
 *   - warrant_redemption_deadline / warrant_redemption_price
 *   - warrant_exercise_price
 *   - warrant_underlying_ticker
 *   - warrant_underlying_price
 *   - warrant_expiration_date  ← added P141·8
 */
import { AlertTriangle, Target, XCircle, Clock } from 'lucide-react';

function pct(current, target) {
  if (current == null || target == null || target === 0) return null;
  return ((current - target) / target) * 100;
}

function daysUntil(iso) {
  if (!iso) return null;
  const exp = new Date(iso + 'T00:00:00Z');
  if (isNaN(exp.getTime())) return null;
  return Math.round((exp.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
}

export default function WarrantAlertStrip({ stock }) {
  if (!stock || !stock.is_warrant) return null;

  const status = (stock.warrant_status || 'active').toLowerCase();
  const exercise = stock.warrant_exercise_price;
  const under = stock.warrant_underlying_price;
  const underTkr = stock.warrant_underlying_ticker;
  const deadline = stock.warrant_redemption_deadline;
  const tender = stock.warrant_redemption_price;
  const expDate = stock.warrant_expiration_date;

  const distancePct = pct(under, exercise);
  const nearExercise = distancePct != null && Math.abs(distancePct) <= 5;
  const isRedeemed = status === 'redeemed';
  const isAnnounced = status === 'redemption_announced';
  const days = daysUntil(expDate);
  // Round P142·3 — countdown alert fires when the warrant has an
  // expiration_date on file AND today is within 30 days of it
  // (positive days only; expired rows are handled separately). We
  // skip when the row is already flagged as redeemed since that's
  // the higher-priority terminal state.
  const nearExpiry = days != null && days >= 0 && days <= 30 && !isRedeemed;

  if (!isRedeemed && !isAnnounced && !nearExpiry && !nearExercise) return null;

  // Redemption trumps everything.
  if (isRedeemed) {
    return (
      <div
        className="mx-3 sm:mx-6 my-2 flex items-center gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-[11px] text-rose-800 dark:text-rose-300"
        data-testid={`warrant-alert-redeemed-${stock.ticker}`}
      >
        <XCircle className="w-3.5 h-3.5 shrink-0" />
        <span className="font-semibold">Force-redeemed</span>
        {deadline && <span>on <span className="font-mono">{deadline}</span></span>}
        {tender != null && <span>· tender <span className="font-mono">${tender.toFixed(2)}</span>/warrant</span>}
        <span className="opacity-80">— position is closed.</span>
      </div>
    );
  }

  if (isAnnounced) {
    return (
      <div
        className="mx-3 sm:mx-6 my-2 flex items-center gap-2 rounded-md border border-orange-500/50 bg-orange-500/10 px-3 py-1.5 text-[11px] text-orange-800 dark:text-orange-300"
        data-testid={`warrant-alert-announced-${stock.ticker}`}
      >
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span className="font-semibold">Redemption 8-K filed</span>
        {deadline && <span>· exercise or sell by <span className="font-mono">{deadline}</span></span>}
        {tender != null && <span>· tender <span className="font-mono">${tender.toFixed(2)}</span>/warrant</span>}
      </div>
    );
  }

  // Round P142·3 — Near-expiry beats near-strike since expiry is
  // a hard deadline (0.01 tender OR total loss) whereas near-strike
  // is a valuation signal that can reverse.
  if (nearExpiry) {
    return (
      <div
        className="mx-3 sm:mx-6 my-2 flex items-center gap-2 rounded-md border border-rose-500/50 bg-rose-500/10 px-3 py-1.5 text-[11px] text-rose-800 dark:text-rose-300"
        data-testid={`warrant-alert-near-expiry-${stock.ticker}`}
      >
        <Clock className="w-3.5 h-3.5 shrink-0" />
        <span className="font-semibold">
          {days === 0 ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`}
        </span>
        {expDate && <span>· <span className="font-mono">{expDate}</span></span>}
        <span className="opacity-80">— exercise or sell before 5:00 p.m. NYC on the date.</span>
      </div>
    );
  }

  // nearExercise
  const direction = distancePct >= 0 ? 'above' : 'below';
  return (
    <div
      className="mx-3 sm:mx-6 my-2 flex items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-[11px] text-amber-800 dark:text-amber-300"
      data-testid={`warrant-alert-near-strike-${stock.ticker}`}
    >
      <Target className="w-3.5 h-3.5 shrink-0" />
      <span className="font-semibold">Near strike</span>
      <span>
        {underTkr && <span className="font-mono">{underTkr}</span>} at
        <span className="font-mono ml-1">${under?.toFixed(2)}</span>
        <span className="mx-1">·</span>
        <span className="font-mono">{Math.abs(distancePct).toFixed(1)}%</span> {direction} strike
        <span className="font-mono ml-1">${exercise?.toFixed(2)}</span>
      </span>
    </div>
  );
}
